import { useState, type ReactNode } from 'react';
import { useGT } from 'gt-react';
import type { DirectLoadSortBy, LinkLoad } from '@erp/rpc';
import {
  appTableFeatures,
  clientAppTableFeatures,
  functionalUpdate,
  useTable,
  type ColumnFiltersState,
  type PaginationState,
  type Row,
  type Updater,
} from '@/lib/tanstack-table';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/common/data-table/data-table';
import { DataTableColumnHeader } from '@/components/common/data-table/data-table-column-header';
import { DataTablePagination } from '@/components/common/data-table/data-table-pagination';
import { DataTableToolbar } from '@/components/common/data-table/data-table-toolbar';
import { DataTableViewOptions } from '@/components/common/data-table/data-table-view-options';
import {
  useServerSort,
  type ServerSortedColumn,
} from '@/components/common/data-table/server-sort';
import { unsortableColumn } from '@/components/common/data-table/unsortable-column';
import {
  MultiSelect,
  type MultiSelectOption,
} from '@/components/common/MultiSelect';
import { useTableColumnSettings } from '@/hooks/useTableColumnSettings';
import { formatDatetimeWithSeconds } from '@/utils/dateUtils';
import { formatDurationMs } from '@/utils/formatDurationMs';
import {
  LOAD_STATUS_VARIANT,
  count,
  elapsedMs,
  throughput,
  useLoadStatusLabels,
} from './loadDisplay';
import {
  DEFAULT_LOADS_QUERY,
  LOAD_STATUSES,
  LOAD_STATUS_COLUMN_ID as STATUS_COLUMN_ID,
  toColumnFilters,
  toDirectLoadSortBy,
  toStatuses,
  type LoadsQuery,
} from './loadsQuery';
import { LoadModeNote } from './LoadModeNote';

/** Resize floor, as on the collection threads table. */
const MIN_COLUMN_SIZE = 60;

const LINK_COLUMN_ID = 'link';

type LoadColumn<L extends LinkLoad> = ServerSortedColumn<L, DirectLoadSortBy>;

/**
 * Server mode: the rows are one page the server searched, filtered and
 * sorted for `query`; the table only shows them and reports changes.
 */
export interface LoadsTableServer {
  /** Loads matching the query over every page. */
  total: number;
  query: LoadsQuery;
  onQueryChange: (update: (previous: LoadsQuery) => LoadsQuery) => void;
}

/**
 * The query the table edits: its own while it processes rows itself, the
 * caller's in server mode. Both hooks always run, so the mode may not change
 * between renders.
 */
function useLoadsQuery(
  server: LoadsTableServer | undefined,
): [LoadsQuery, LoadsTableServer['onQueryChange']] {
  const [own, setOwn] = useState(DEFAULT_LOADS_QUERY);
  return server ? [server.query, server.onQueryChange] : [own, setOwn];
}

/** A leading column naming each load's link (the loads page shows it). */
export interface LoadsTableLinkColumn<L extends LinkLoad> {
  title: string;
  /** Sort, filter and search value. */
  value: (load: L) => string;
  cell: (load: L) => ReactNode;
}

/** Status facet: keeps rows whose status is one of the selected ones. */
function statusIsSelected<L extends LinkLoad>(
  row: Row<L>,
  _columnId: string,
  filterValue: unknown,
): boolean {
  return (
    !Array.isArray(filterValue) ||
    filterValue.length === 0 ||
    filterValue.includes(row.original.status)
  );
}

/** Lowercased text the search box matches: ids, source file, error, link. */
function loadSearchText<L extends LinkLoad>(
  load: L,
  linkValue?: (load: L) => string,
): string {
  return [load.loadId, load.sourceUri, load.error, linkValue?.(load)]
    .filter((part): part is string => Boolean(part))
    .join('\n')
    .toLowerCase();
}

/** ISO timestamp → epoch ms, undefined when absent so it sorts last. */
function timeValue(iso: string | null): number | undefined {
  return iso ? Date.parse(iso) : undefined;
}

function useLoadColumns<L extends LinkLoad>({
  now,
  linkColumn,
  action,
}: {
  now: number;
  linkColumn?: LoadsTableLinkColumn<L>;
  action?: (load: L) => ReactNode;
}): { columns: LoadColumn<L>[]; labels: Record<string, string> } {
  const gt = useGT();
  const statusLabel = useLoadStatusLabels();

  const labels: Record<string, string> = {
    loadId: gt('Load', { context: 'direct load history column' }),
    startedAt: gt('Started', { context: 'direct load history column' }),
    finishedAt: gt('Finished', { context: 'direct load history column' }),
    duration: gt('Duration', {
      context:
        'direct load history column: elapsed time, still counting while the load runs',
    }),
    status: gt('Status', { context: 'direct load history column' }),
    chunks: gt('Chunks ok / failed / total', {
      context: 'direct load history column: chunks succeeded / failed / total',
    }),
    rowsRead: gt('Rows read', { context: 'direct load history column' }),
    rowsFailed: gt('Failed', { context: 'direct load history column' }),
    rowsTransformed: gt('Transformed', {
      context: 'direct load history column: rows the transform applied',
    }),
    rowsSkipped: gt('Skipped', { context: 'direct load history column' }),
    throughput: gt('Rows/s', {
      context: 'direct load history column: throughput',
    }),
  };

  const numberColumn = (
    id: 'rowsRead' | 'rowsFailed' | 'rowsTransformed' | 'rowsSkipped',
  ): LoadColumn<L> => ({
    id,
    accessorFn: (load) => load[id],
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={labels[id]} />
    ),
    cell: ({ row }) => (
      <span className="text-xs">{count(row.original[id])}</span>
    ),
    meta: { align: 'right' },
    enableGlobalFilter: false,
    size: 110,
  });

  const columns: LoadColumn<L>[] = [
    ...(linkColumn
      ? [
          {
            id: LINK_COLUMN_ID,
            accessorFn: (load: L) => linkColumn.value(load),
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title={linkColumn.title} />
            ),
            cell: ({ row }) => (
              <span className="whitespace-nowrap text-xs">
                {linkColumn.cell(row.original)}
              </span>
            ),
            // Not hideable, so the reorder grip skips it and it stays the
            // first column: the shared DataTable has no sticky pinning.
            enableHiding: false,
            size: 200,
          } satisfies LoadColumn<L>,
        ]
      : []),
    {
      id: 'loadId',
      accessorFn: (load) => load.loadId,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={labels.loadId} />
      ),
      cell: ({ row }) => (
        <span
          className="block max-w-[16rem] truncate font-mono text-xs"
          title={`${row.original.loadId}\n${row.original.sourceUri}`}
        >
          {row.original.loadId}
        </span>
      ),
      size: 240,
    },
    {
      id: 'startedAt',
      accessorFn: (load) => timeValue(load.startedAt),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={labels.startedAt} />
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs">
          {row.original.startedAt
            ? formatDatetimeWithSeconds(row.original.startedAt)
            : '—'}
        </span>
      ),
      sortUndefined: 'last',
      enableGlobalFilter: false,
      size: 170,
    },
    {
      id: 'finishedAt',
      accessorFn: (load) => timeValue(load.finishedAt),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={labels.finishedAt} />
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs">
          {row.original.finishedAt
            ? formatDatetimeWithSeconds(row.original.finishedAt)
            : '—'}
        </span>
      ),
      sortUndefined: 'last',
      enableGlobalFilter: false,
      size: 170,
    },
    {
      id: 'duration',
      accessorFn: (load) => elapsedMs(load, now) ?? undefined,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={labels.duration} />
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs">
          {formatDurationMs(elapsedMs(row.original, now)) ?? '—'}
        </span>
      ),
      meta: { align: 'right' },
      sortUndefined: 'last',
      enableGlobalFilter: false,
      size: 110,
    },
    {
      id: STATUS_COLUMN_ID,
      accessorFn: (load) => load.status,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={labels.status} />
      ),
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1">
            <Badge variant={LOAD_STATUS_VARIANT[row.original.status]}>
              {statusLabel[row.original.status]}
            </Badge>
            <LoadModeNote
              mode={row.original.mode}
              declinedReason={row.original.sqlModeDeclinedReason}
            />
          </div>
          {row.original.error && (
            <p
              className="mt-1 max-w-[20rem] truncate text-xs text-destructive"
              title={row.original.error}
            >
              {row.original.error}
            </p>
          )}
        </div>
      ),
      filterFn: statusIsSelected,
      enableGlobalFilter: false,
      size: 160,
    },
    {
      id: 'chunks',
      accessorFn: (load) => load.chunks,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={labels.chunks} />
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs">
          {count(row.original.chunksSucceeded)} /{' '}
          {count(row.original.chunksFailed)} / {count(row.original.chunks)}
        </span>
      ),
      meta: { align: 'right' },
      enableGlobalFilter: false,
      size: 170,
    },
    numberColumn('rowsRead'),
    numberColumn('rowsFailed'),
    numberColumn('rowsTransformed'),
    numberColumn('rowsSkipped'),
    {
      id: 'throughput',
      accessorFn: (load) => throughput(load, now) ?? undefined,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={labels.throughput} />
      ),
      cell: ({ row }) => {
        const rate = throughput(row.original, now);
        return (
          <span className="text-xs">
            {rate === null ? '—' : count(Math.round(rate))}
          </span>
        );
      },
      meta: { align: 'right' },
      sortUndefined: 'last',
      enableGlobalFilter: false,
      size: 100,
    },
    ...(action
      ? [
          // A utility column: the row's Cancel button.
          unsortableColumn<L>({
            id: 'actions',
            header: () => null,
            cell: ({ row }) => (
              <div className="text-right">{action(row.original)}</div>
            ),
            enableHiding: false,
            enableResizing: false,
            enableGlobalFilter: false,
            size: 90,
            minSize: 90,
          }),
        ]
      : []),
  ];

  if (linkColumn) labels[LINK_COLUMN_ID] = linkColumn.title;
  return { columns, labels };
}

/**
 * Direct loads, one row each, on the shared data table the collection
 * threads list uses: search, a status facet, sortable and
 * reorderable/resizable columns with the layout saved per user under
 * `tableId`, paging, and horizontal scroll inside the table. Without
 * `server` the rows are the whole list and the table searches, filters,
 * sorts and pages them itself; with it they are one server page and every
 * change goes back through `server.onQueryChange`. `linkColumn` adds a
 * leading column (the loads page shows which link each load is for);
 * `action` adds a trailing one (its Cancel button).
 */
export function LoadsTable<L extends LinkLoad>({
  tableId,
  loads,
  now,
  linkColumn,
  action,
  server,
}: {
  /** Key the per-user column layout is saved under. */
  tableId: string;
  loads: L[];
  /** When `loads` was fetched: the end of a running load's throughput window. */
  now: number;
  linkColumn?: LoadsTableLinkColumn<L>;
  action?: (load: L) => ReactNode;
  server?: LoadsTableServer;
}) {
  const gt = useGT();
  const statusLabel = useLoadStatusLabels();
  const { columns, labels } = useLoadColumns({ now, linkColumn, action });
  const {
    columnOrder,
    columnVisibility,
    columnSizing,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onColumnSizingChange,
    resetLayout,
  } = useTableColumnSettings(tableId);
  const [query, setQuery] = useLoadsQuery(server);

  // Shift-click a header to add it as a further sort level. A filter or sort
  // change starts over from the first page; paging does not.
  const serverSort = useServerSort<L, DirectLoadSortBy>({
    columns,
    toSortBy: toDirectLoadSortBy,
    sort: query.sort,
    onSortChange: (sort) => setQuery((q) => ({ ...q, sort, pageIndex: 0 })),
  });
  // Without `server` the table sorts the rows itself, so only server mode
  // leaves their order to the server.
  const { manualSorting, ...sortOptions } = serverSort.tableOptions;
  function onColumnFiltersChange(updater: Updater<ColumnFiltersState>) {
    setQuery((q) => ({
      ...q,
      statuses: toStatuses(
        functionalUpdate(updater, toColumnFilters(q.statuses)),
      ),
      pageIndex: 0,
    }));
  }
  function onGlobalFilterChange(updater: Updater<string>) {
    setQuery((q) => ({
      ...q,
      search: functionalUpdate(updater, q.search),
      pageIndex: 0,
    }));
  }
  function onPaginationChange(updater: Updater<PaginationState>) {
    setQuery((q) => ({
      ...q,
      ...functionalUpdate(updater, {
        pageIndex: q.pageIndex,
        pageSize: q.pageSize,
      }),
    }));
  }

  const linkValue = linkColumn?.value;
  const table = useTable({
    features: server ? appTableFeatures : clientAppTableFeatures,
    ...sortOptions,
    columns: serverSort.columns,
    data: loads,
    getRowId: (load) => load.loadId,
    state: {
      sorting: serverSort.sorting,
      columnFilters: toColumnFilters(query.statuses),
      globalFilter: query.search,
      pagination: { pageIndex: query.pageIndex, pageSize: query.pageSize },
      columnOrder,
      columnVisibility,
      columnSizing,
    },
    onColumnFiltersChange,
    onGlobalFilterChange,
    onPaginationChange,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onColumnSizingChange,
    globalFilterFn: (row: Row<L>, _columnId: string, filterValue: unknown) =>
      loadSearchText(row.original, linkValue).includes(
        String(filterValue).toLowerCase(),
      ),
    // The list refetches every 5 s while a load runs; a refetch must not
    // throw the reader back to the first page. Filters reset it explicitly.
    autoResetPageIndex: false,
    ...(server
      ? {
          manualPagination: true,
          manualSorting,
          manualFiltering: true,
          rowCount: server.total,
        }
      : {}),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    defaultColumn: { minSize: MIN_COLUMN_SIZE },
  });

  // A refetch can shrink the list under the current page (a filter kept
  // matching fewer loads, say), which would show an empty page while matches
  // remain. Adjusting state during render re-renders before anything paints;
  // in server mode the owner of the query does this with the server's total.
  const lastPageIndex = Math.max(table.getPageCount() - 1, 0);
  if (!server && query.pageIndex > lastPageIndex) {
    setQuery((q) => ({ ...q, pageIndex: lastPageIndex }));
  }

  const statusOptions: MultiSelectOption[] = LOAD_STATUSES.map((status) => ({
    id: status,
    label: statusLabel[status],
  }));
  const selectedStatuses = new Set<string>(query.statuses);

  return (
    <div className="space-y-3">
      <Card className="p-3">
        <DataTableToolbar table={table}>
          <Input
            placeholder={gt('Search loads...', {
              context: 'direct loads table search placeholder',
            })}
            value={query.search}
            onChange={(event) => table.setGlobalFilter(event.target.value)}
            className="h-8 w-[200px]"
          />
          <MultiSelect
            options={statusOptions}
            value={statusOptions.filter((o) => selectedStatuses.has(o.id))}
            onChange={(options) => {
              const ids = options.map((o) => o.id);
              table
                .getColumn(STATUS_COLUMN_ID)
                ?.setFilterValue(ids.length > 0 ? ids : undefined);
            }}
            placeholder={gt('Status', {
              context: 'direct loads table status filter placeholder',
            })}
            size="sm"
            shouldAutoSort={false}
            className="h-8"
          />
          <DataTableViewOptions
            table={table}
            columnLabels={labels}
            onResetLayout={resetLayout}
          />
        </DataTableToolbar>
      </Card>
      <DataTable
        table={table}
        enableColumnReorder
        emptyMessage={gt('No loads match these filters', {
          context: 'direct loads table empty state',
        })}
      />
      <DataTablePagination table={table} />
    </div>
  );
}
